import { getAvailableLanguages } from '../Languages';

/**
 * Normalize a stored auto-translation language list from the API.
 * null/undefined/[] → null (all languages enabled).
 */
export function normalizeAutoTranslationLanguages(selected) {
  if (selected == null) return null;
  let value = selected;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (_) {
      return null;
    }
  }
  if (!Array.isArray(value)) return null;
  const allowed = new Set(getAvailableLanguages().map((l) => l.code));
  const codes = [...new Set(
    value
      .map((c) => String(c || '').toLowerCase().split('-')[0])
      // Map dialect codes to their parent family (gsw/wae → de)
      .map((c) => (c === 'gsw' || c === 'wae') ? 'de' : c)
      .filter((c) => c && allowed.has(c))
  )];
  return codes.length > 0 ? codes : null;
}

/**
 * Whether an incoming message should be auto-translated for the receiver.
 * - gsw/wae are grouped under 'de': enabling 'de' covers all German dialects.
 * - Same language family as receiver → allow only if that family is in the allowlist
 *   (covers dialect-to-standard translation, e.g. Swiss German → Hochdeutsch).
 * - null/empty allowlist → translate all foreign languages.
 * - Non-empty allowlist → only translate languages whose family is IN the list.
 * - Unknown source (null detectedMessageLanguage) → always allow.
 */
export function shouldAutoTranslateFromSender(
  messageLang,
  receiverLang,
  enabledLanguages,
  _messageMatchesSenderLanguage
) {
  const source = messageLang ? String(messageLang).toLowerCase().split('-')[0] : null;
  const receiver = receiverLang ? String(receiverLang).toLowerCase().split('-')[0] : 'en';
  const receiverFamily = (receiver === 'gsw' || receiver === 'wae') ? 'de' : receiver;
  const sourceFamily = (source === 'gsw' || source === 'wae') ? 'de' : source;

  if (!enabledLanguages || !Array.isArray(enabledLanguages) || enabledLanguages.length === 0) {
    // No allowlist → translate everything foreign, skip same family
    if (source && sourceFamily === receiverFamily) return false;
    return true;
  }
  if (!source) return true;
  // Check allowlist using the family code (gsw/wae → de)
  return enabledLanguages.includes(sourceFamily);
}

/**
 * Resolve the checkbox selection for the settings UI.
 * null (all enabled) → all language codes.
 */
export function resolveAutoTranslationSelection(enabledLanguages, _userLang) {
  const all = getAvailableLanguages().map((l) => l.code);
  if (enabledLanguages == null) {
    return all;
  }
  return enabledLanguages.filter((c) => c !== undefined && all.includes(c));
}

/**
 * Lightweight client-side language detection using word markers.
 * Used to gate translation for encrypted messages (where BE detection is null).
 * Returns a 2-letter language code or null if undetermined.
 */
const CLIENT_LANG_MARKERS = {
  de: ['der', 'die', 'das', 'und', 'nicht', 'ich', 'ist', 'ein', 'eine', 'mit', 'auf', 'für', 'auch', 'von', 'den', 'dem', 'zu', 'sich', 'wir', 'sie', 'dass', 'oder', 'aber', 'noch', 'schon', 'haben', 'werden', 'kann', 'bitte', 'danke', 'heute', 'morgen', 'guten'],
  en: ['the', 'and', 'you', 'that', 'was', 'for', 'are', 'with', 'this', 'have', 'from', 'not', 'but', 'they', 'will', 'would', 'there', 'what', 'about', 'which', 'when', 'your', 'can', 'just', 'please', 'thanks', 'hello', 'today'],
  fr: ['le', 'la', 'les', 'des', 'une', 'est', 'pas', 'que', 'pour', 'dans', 'qui', 'sur', 'avec', 'nous', 'vous', 'je', 'tu', 'il', 'elle', 'mais', 'plus', 'bonjour', 'merci'],
  es: ['el', 'los', 'las', 'una', 'por', 'con', 'para', 'que', 'del', 'como', 'más', 'pero', 'muy', 'todo', 'esta', 'ser', 'también', 'fue', 'hola', 'gracias'],
  it: ['il', 'gli', 'della', 'che', 'per', 'con', 'sono', 'una', 'anche', 'questo', 'come', 'più', 'loro', 'stato', 'essere', 'molto', 'ciao', 'grazie'],
  pt: ['que', 'não', 'uma', 'para', 'com', 'como', 'mais', 'por', 'dos', 'das', 'foi', 'isso', 'ser', 'está', 'também', 'muito', 'bem', 'são', 'olá', 'obrigado'],
  nl: ['het', 'een', 'van', 'dat', 'met', 'voor', 'niet', 'zijn', 'ook', 'maar', 'nog', 'wel', 'bij', 'naar', 'deze', 'wordt', 'heeft', 'kan', 'hallo', 'dank'],
  pl: ['nie', 'jest', 'się', 'na', 'to', 'co', 'jak', 'tak', 'ale', 'za', 'już', 'też', 'tylko', 'był', 'bardzo', 'może', 'aby', 'są', 'jego', 'przy', 'będzie', 'wszystko', 'dzień', 'proszę', 'dziękuję'],
  tr: ['bir', 'bu', 'için', 'ile', 'çok', 'var', 'daha', 'olan', 'gibi', 'ben', 'olarak', 'kadar', 'sonra', 'ama', 'biz', 'değil', 'merhaba', 'teşekkür'],
  ru: ['это', 'что', 'как', 'для', 'все', 'они', 'так', 'было', 'очень', 'уже', 'при', 'будет', 'его', 'тоже', 'или', 'быть', 'здравствуйте', 'спасибо'],
};

const DIALECT_MARKERS_GSW = ['isch', 'nöd', 'nid', 'hät', 'het', 'gsi', 'chönd', 'händ', 'chli', 'gmacht', 'gsäit', 'gseit', 'chunt', 'uf', 'mier', 'alli', 'öpper', 'nüt', 'scho', 'gli', 'eus', 'dis', 'bim', 'em', 'grad', 'wäg', 'obe', 'ine', 'mol', 'guet', 'lueg', 'gäll', 'grüezi'];
const DIALECT_MARKERS_WAE = ['ischt', 'nit', 'hiä', 'giä', 'gää', 'mu', 'ds', 'äs', 'heit', 'wier', 'üs', 'titsch', 'gseit', 'chunt', 'eppis', 'schii', 'wärche', 'hie', 'drii', 'züe', 'ab', 'grüessech'];

export function detectLanguageClient(text) {
  if (!text || typeof text !== 'string') return null;
  const clean = text.replace(/<[^>]+>/g, ' ').replace(/&[a-z]+;/gi, ' ');
  const letters = clean.replace(/[^\p{L}\p{M}]+/gu, '');
  if (letters.length < 4) return null;

  const words = clean.toLowerCase().replace(/[^\p{L}\p{M}\s']/gu, ' ').split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  const wordSet = new Set(words);
  const denom = Math.min(words.length, 16);

  let bestCode = null;
  let bestScore = 0;
  let secondScore = 0;
  for (const [code, list] of Object.entries(CLIENT_LANG_MARKERS)) {
    let hits = 0;
    for (const m of list) { if (wordSet.has(m)) hits += 1; }
    const score = hits / denom;
    if (score > bestScore) { secondScore = bestScore; bestScore = score; bestCode = code; }
    else if (score > secondScore) { secondScore = score; }
  }

  if (!bestCode || bestScore < 0.08) return null;
  if (bestScore < secondScore + 0.04 && secondScore > 0) return null;

  // If detected as German, check for Swiss German / Walliserdeutsch dialect markers
  if (bestCode === 'de') {
    let waeHits = 0, gswHits = 0;
    for (const m of DIALECT_MARKERS_WAE) { if (wordSet.has(m)) waeHits++; }
    for (const m of DIALECT_MARKERS_GSW) { if (wordSet.has(m)) gswHits++; }
    if (waeHits >= 2) return 'wae';
    if (gswHits >= 2) return 'gsw';
  }

  return bestCode;
}

/**
 * Payload for PUT /user/autoTranslationLanguages.
 * If every language is selected → null (all enabled).
 */
export function selectionToApiPayload(selectedCodes, _userLang) {
  const all = getAvailableLanguages().map((l) => l.code);
  const selected = [...new Set(
    (selectedCodes || [])
      .map((c) => String(c || '').toLowerCase().split('-')[0])
      .filter((c) => c && all.includes(c))
  )];
  if (selected.length === all.length) return null;
  return selected;
}
